import {
  DatePeriodInput,
  LoadingOverlay,
  setupValidateOpeningsHoursMatch,
} from "@greeter/matter";
import { FailedSection } from "./FailedSection";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonAlert,
} from "@ionic/react";
import { FloatingSaveButton } from "@greeter-venue/components/FloatingSaveButton";
import { SummaryTable } from "@greeter-venue/components/SummaryTable";
import { useActor, useSelector } from "@xstate/react";
import {
  ActivePeriodsChange,
  activePeriodsEditorMachine,
  EditorMode,
} from "./ActivePeriodsEditorMachine";
import * as uuid from "uuid";
import { ActivePeriod, WeeklyOpeningHours } from "@greeter/core";
import { HollowButton } from "@greeter-venue/components/HollowButton";
import { DeleteButton } from "@greeter-venue/components/EditItemToolbar";
import * as SetUtil from "@greeter/set";
import { IVenueApi } from "@greeter/venue-api";
import { arrowBackOutline } from "ionicons/icons";
import { Log } from "@greeter/log";
import css from "./ActivePeriodsEditor.module.scss";

const dateFormatter = Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  year: "numeric",
  month: "short",
  hourCycle: "h24",
  hour: "2-digit",
  minute: "2-digit",
});

/**
 * A summation of the changes that has happened in the editor
 */
export type ActivePeriodsChangeset = {
  toSave: Array<ActivePeriod>;
  toDelete: Array<string>;
};

export function findActivePeriodsToDelete(
  changes: Array<ActivePeriodsChange>
): Array<string> {
  const added = new Set(
    changes.filter((c) => c.type === "add").map((c) => c.data.id)
  );
  const deleted = new Set(
    changes.filter((c) => c.type === "delete").map((c) => c.data.id)
  );
  const toDelete = SetUtil.leftDifference(deleted, added);
  return Array.from(toDelete);
}

export interface ActivePeriodsEditorApiRequirements
  extends Pick<
    IVenueApi,
    "fetchActivePeriods" | "fetchVenue" | "fetchSpecialOpeningHours"
  > {}

export type ActivePeriodsEditorProps = {
  mode: EditorMode;
  productId: string;
  onChange?: (changes: ActivePeriodsChangeset) => void;
  api: ActivePeriodsEditorApiRequirements;
};

export function ActivePeriodsEditor(props: ActivePeriodsEditorProps) {
  const [snap, send, m] = useActor(activePeriodsEditorMachine, {
    input: { productId: props.productId, mode: props.mode, api: props.api },
  });
  const activePeriods = useSelector(m, (s) =>
    Array.from(s.context.changes.value.values())
  );

  const activePeriodsRef = useSelector(m, (s) => {
    return s.context.activePeriodsRef?.getSnapshot();
  });

  function change(c: ActivePeriodsChange) {
    return send({ type: "change", data: c });
  }

  const changeset: ActivePeriodsChangeset = useSelector(m, (m) => {
    return {
      toSave: Array.from(m.context.changes.value.values()),
      toDelete: findActivePeriodsToDelete(m.context.changes.changes),
    };
  });

  const [alert] = useIonAlert();

  const oh =
    snap.context.venueRef.getSnapshot().context.data?.bookableOpeningHours ??
    new WeeklyOpeningHours();
  const soh = snap.context.specialOpeningHoursRef.getSnapshot().context.data;
  let lowest: Date | undefined;
  let highest: Date | undefined;
  for (let i = 0, l = activePeriods.length; i < l; i++) {
    const activePeriod = activePeriods[i];
    if (!lowest || activePeriod.period.from < lowest)
      lowest = activePeriod.period.from;
    if (!lowest || activePeriod.period.to < lowest)
      lowest = activePeriod.period.to;
    if (!highest || activePeriod.period.from > highest)
      highest = activePeriod.period.from;
    if (!highest || activePeriod.period.to > highest)
      highest = activePeriod.period.to;
  }

  return (
    <div className={css.ActivePeriodsEditor}>
      {activePeriodsRef?.matches("fetching") && (
        <div style={{ height: 100, width: "100%", position: "relative" }}>
          <LoadingOverlay />
        </div>
      )}
      {activePeriodsRef?.matches("failed") && (
        <FailedSection
          reason="Kunne ikke hente aktive perioder"
          onClick={() => send({ type: "fetchActivePeriods" })}
        />
      )}
      {activePeriodsRef?.matches("idle") && activePeriods.length > 0 && (
        <SummaryTable
          headers={["Fra", "Til"]}
          rows={activePeriods.map((ap) => {
            return [
              dateFormatter.format(ap.period.from),
              dateFormatter.format(ap.period.to),
            ];
          })}
          onClick={() => send({ type: "open" })}
        />
      )}
      {(props.mode === "create" ||
        (activePeriodsRef && activePeriodsRef?.matches("idle"))) && (
        <HollowButton
          data-testid="open-active-periods-editor"
          onClick={() => send({ type: "open" })}
        >
          + Tilføj tidsperiode
        </HollowButton>
      )}
      <IonModal
        isOpen={snap.matches("opened")}
        onDidDismiss={() => send({ type: "close" })}
        onWillDismiss={() => send({ type: "close" })}
      >
        <IonHeader>
          <IonToolbar
            style={{
              "--background": "var(--gm-color-light-primary)",
              "--color": "var(--gm-color-dark-primary)",
            }}
          >
            <IonButtons slot="start">
              <IonButton
                onClick={() => {
                  alert({
                    mode: "ios",
                    message:
                      "Hvis du går tilbage uden at gemme vil ændringerne gå tabt",
                    buttons: [
                      {
                        text: "Gem",
                        handler() {
                          if (props.onChange) props.onChange(changeset);
                          send({ type: "close" });
                        },
                      },
                      {
                        text: "Fortsæt",
                        handler() {
                          send({ type: "close" });
                        },
                      },
                    ],
                  });
                }}
              >
                <IonIcon slot="icon-only" icon={arrowBackOutline} />
              </IonButton>
            </IonButtons>
            <IonTitle>Aktive tidsperioder</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent
          style={{
            "--background": "var(--gm-color-light-primary)",
          }}
        >
          <div className={css.ActivePeriods}>
            {activePeriods.length === 0 ? (
              <HollowButton
                data-testid="new-active-period"
                onClick={() =>
                  change({
                    type: "add",
                    data: {
                      id: uuid.v7(),
                    },
                  })
                }
              >
                + Tilføj tidsperiode
              </HollowButton>
            ) : (
              <div className={css.ActivePeriodItems}>
                {activePeriods.map((ap) => {
                  return (
                    <div key={ap.id} className={css.ActivePeriodItem}>
                      <DatePeriodInput
                        // NOTE: Do not pass ap directly as it will not run validation correctly. Slightly hacking to ensure validation is run
                        period={{ from: ap.period.from, to: ap.period.to }}
                        validators={[
                          setupValidateOpeningsHoursMatch({
                            highest,
                            lowest,
                            openingHours: oh,
                            specialOpeningHours: soh,
                            onMatchTo(date) {
                              change({
                                type: "setEndsAt",
                                data: { id: ap.id, date },
                              });
                            },
                            onMatchFrom(date) {
                              change({
                                type: "setStartsAt",
                                data: { id: ap.id, date },
                              });
                            },
                          }),
                        ]}
                        onChange={(changed) => {
                          try {
                            change({
                              type: "setStartsAt",
                              data: { id: ap.id, date: changed.from },
                            });
                            change({
                              type: "setEndsAt",
                              data: { id: ap.id, date: changed.to },
                            });
                          } catch {
                            Log.warn(
                              "[ActivePeriodsEditor][DatePeriodInput][onChange] Failed to parse the date period. Probably an intermediate state",
                              changed
                            );
                          }
                        }}
                      />
                      <DeleteButton
                        onClick={() =>
                          change({
                            type: "delete",
                            data: { id: ap.id },
                          })
                        }
                      />
                    </div>
                  );
                })}
                <HollowButton
                  data-testid="new-active-period"
                  style={{ width: "100%" }}
                  onClick={() =>
                    change({
                      type: "add",
                      data: {
                        id: uuid.v7(),
                      },
                    })
                  }
                >
                  + Tilføj tidsperiode
                </HollowButton>
              </div>
            )}
          </div>
        </IonContent>
        <FloatingSaveButton
          onClick={() => {
            if (props.onChange) {
              props.onChange(changeset);
            }
            send({ type: "close" });
          }}
          data-testid="save-active-periods"
          show={changeset.toDelete.length > 0 || changeset.toSave.length > 0}
        />
      </IonModal>
    </div>
  );
}
